import isEqual from 'lodash/isEqual'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

function resolveSetState<T>(update: SetStateAction<T>, current: T): T {
  if (typeof update === 'function') {
    return (update as (value: T) => T)(current)
  }
  return update
}

export function useMultiState<T>(
  keys: ReadonlySet<string>,
  defaultValue: (key: string) => T
): ReadonlyMap<string, [T, Dispatch<SetStateAction<T>>]> {
  const [state, setState] = useState<ReadonlyMap<string, T>>(
    () => new Map(keys.values().map((key) => [key, defaultValue(key)]))
  )

  // If we add or remove a key, update the map
  useEffect(() => {
    setState((prev) => {
      if (isEqual(keys, new Set(prev.keys()))) {
        return prev
      }

      const next = new Map()
      for (const key of keys.values()) {
        next.set(key, prev.get(key) ?? defaultValue(key))
      }
      return next
    })
  }, [defaultValue, keys])

  const createSetter = useCallback(
    (key: string) => {
      return (val: SetStateAction<T>) => {
        setState((prev) => {
          const prevVal = prev.get(key) ?? defaultValue(key)
          const newVal = resolveSetState(val, prevVal)
          if (isEqual(prevVal, newVal)) {
            return prev
          }
          const updated = new Map(prev)
          updated.set(key, newVal)
          return updated
        })
      }
    },
    [defaultValue]
  )

  return useMemo(() => {
    const tupleEntries = [
      ...state
        .entries()
        .map(
          ([key, pagination]) =>
            [key, [pagination, createSetter(key)]] as [
              string,
              [T, Dispatch<SetStateAction<T>>],
            ]
        ),
    ]
    return new Map([...tupleEntries]) as ReadonlyMap<
      string,
      [T, Dispatch<SetStateAction<T>>]
    >
  }, [state, createSetter])
}
